// ⚠️ Client com refreshToken – NÃO remover sem rever expiração do accessToken

import axios from "axios";
import safeJSON from "../../components/safeJSON";
import {
  UPGRADE_ROUTE,
  clearSubscriptionStatus,
  setSubscriptionStatus,
} from "../subscription/subscriptionState";


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


export const api = axios.create({
  baseURL: API_BASE_URL,
});

// ---------- Helpers de token ----------
function getAccessToken() {
  const accessToken = localStorage.getItem("accessToken");
  if (accessToken) return accessToken;
}

function getRefreshToken() {
  return localStorage.getItem("refreshToken");
}

function saveTokens(accessToken, refreshToken) {
  if (accessToken) {
    localStorage.setItem("accessToken", accessToken);
  }
  if (refreshToken) {
    localStorage.setItem("refreshToken", refreshToken);
  }
}

function clearAuthAndRedirect() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("token");
  clearSubscriptionStatus();
  // Se você salvar mais coisas de sessão, pode limpar aqui também
  // localStorage.removeItem("tenant");
  // localStorage.removeItem("activeRestaurant");
  window.location.href = "/login";
}

// ---------- Interceptor de REQUEST ----------
// Inclui accessToken, tenant_slug e restaurante ativo em TODA requisição
api.interceptors.request.use((config) => {
  const token = getAccessToken();

  const tenant = safeJSON("tenant");
  const activeRestaurant = safeJSON("activeRestaurant");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (tenant?.slug) {
    config.headers["x-tenant-slug"] = tenant.slug;
  }

  if (activeRestaurant?.id) {
    config.headers["x-restaurant-id"] = activeRestaurant.id;
  }

  return config;
});

// ---------- Interceptor de RESPONSE com refresh automático ----------
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Se não for 401, só repassa o erro
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Evita loop infinito
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    // Se já tem um refresh em andamento, coloca essa requisição na fila
    if (isRefreshing) {
      return new Promise(function (resolve, reject) {
        failedQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          originalRequest.headers.Authorization = "Bearer " + newToken;
          return api(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Usa axios "cru" para não cair no mesmo interceptor e gerar loop
      const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {
        refreshToken: refreshToken,
      });

      const newAccessToken = refreshResponse.data.accessToken;
      const newRefreshToken = refreshResponse.data.refreshToken;

      saveTokens(newAccessToken, newRefreshToken);

      // Atualiza header padrão do client
      //api.defaults.headers.Authorization = "Bearer " + newAccessToken;
      api.defaults.headers.common["Authorization"] = "Bearer " + newAccessToken;


      // Libera as requisições que estavam esperando o refresh
      processQueue(null, newAccessToken);

      // Reenvia a requisição original com o novo token
      originalRequest.headers.Authorization = "Bearer " + newAccessToken;
      return api(originalRequest);
    } catch (err) {
      // Falhou o refresh → desloga geral
      processQueue(err, null);

      // Apenas desloga se realmente for token inválido
      const msg = err.response?.data?.error;

      if (err.response?.status === 403 && msg === "Refresh token inválido ou expirado.") {
        clearAuthAndRedirect();
      }

      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

// Subscription gate (402): flags the session as blocked and REPLACES the
// current history entry with the upgrade screen, so browser Back cannot
// return to the page the API has just denied.
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 402) {
      setSubscriptionStatus("expired");

      if (window.location.pathname !== UPGRADE_ROUTE) {
        window.location.replace(UPGRADE_ROUTE);
      }
    }
    return Promise.reject(err);
  }
);

